import { BRAND_NAMES } from '@hyphengroup/cms-components/modules/segment';
import { ExclusiveOfferTypeEnum } from '@hyphengroup/cms-components/types/cmsEnums';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { Translated } from '@hyphengroup/cms-components/utils/translations/staticText';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { useMemo } from 'react';
import { RewardCardTranslations } from './i18n';
import { ItemListRewardCard } from './ItemListRewardCard';
import { useClientContext } from '$/modules/context/client';

type RewardListProps = CmsProduct & {
  id: string;
  displayFlash: boolean;
  staticText: Translated<typeof RewardCardTranslations>;
};

export const RewardList: React.FC<RewardListProps> = ({
  id,
  welcome_offers,
  exclusive_offers,
  displayFlash,
  staticText,
}) => {
  const context = useClientContext();

  const flashGifts = useMemo(
    () =>
      exclusive_offers
        ?.filter(
          (offer) =>
            offer.exclusive_offer_type === ExclusiveOfferTypeEnum.Flash,
        )
        .flatMap(
          (offer) =>
            offer.list_of_gifts?.map((giftList) => giftList.gift) || [],
        ) ?? [],
    [exclusive_offers],
  );

  const exclusiveGiftsWithoutFlash = useMemo(
    () =>
      exclusive_offers
        ?.filter(
          (offer) =>
            offer.exclusive_offer_type !== ExclusiveOfferTypeEnum.Flash,
        )
        .flatMap(
          (offer) =>
            offer.list_of_gifts?.map((giftList) => giftList.gift) || [],
        ) ?? [],
    [exclusive_offers],
  );

  const welcomeGifts = useMemo(
    () =>
      welcome_offers?.flatMap(
        (offer) => offer.list_of_gifts?.map((giftList) => giftList.gift) || [],
      ) ?? [],
    [welcome_offers],
  );

  return (
    <div
      id={id}
      className="flex border-collapse flex-col gap-spacing/padding/y rounded-b-radius/card border border-borders/default px-spacing/gap/x py-spacing/gap/y-sm"
    >
      {displayFlash && flashGifts.length > 0 && (
        <div>
          {flashGifts.map(
            (gift) =>
              gift && (
                <ItemListRewardCard
                  key={gift.uid ?? gift.gift_title}
                  staticText={staticText}
                  {...gift}
                />
              ),
          )}
        </div>
      )}
      {exclusiveGiftsWithoutFlash.length > 0 && !displayFlash && (
        <div className="flex flex-col gap-spacing/padding/y">
          <div className="mhg-alpha-btn-small flex items-center text-buttons/neutral">
            {tr('exclusiveOffers', staticText, {
              brand: BRAND_NAMES[context.brand],
            })}
          </div>
          {exclusiveGiftsWithoutFlash.map(
            (gift) =>
              gift && (
                <ItemListRewardCard
                  key={gift.uid ?? gift.gift_title}
                  staticText={staticText}
                  {...gift}
                />
              ),
          )}
        </div>
      )}
      {welcomeGifts.length > 0 && !displayFlash && (
        <div className="flex flex-col gap-spacing/padding/y">
          <div className="mhg-alpha-btn-small flex items-center text-buttons/neutral">
            {tr('bankDeals', staticText)}
          </div>
          {welcomeGifts.map(
            (gift) =>
              gift && (
                <ItemListRewardCard
                  key={gift.uid ?? gift.gift_title}
                  staticText={staticText}
                  {...gift}
                />
              ),
          )}
        </div>
      )}
    </div>
  );
};
